import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";

const formFields = {
  companyType: [
    { value: "voip-operator", label: "VoIP operator / carrier" },
    { value: "reseller", label: "Reseller / MSP" },
    { value: "cpaas", label: "CPaaS platform" },
    { value: "contact-center", label: "Contact center" },
    { value: "isp", label: "ISP / telco" },
    { value: "other", label: "Other" },
  ],
  monthlyVolume: [
    { value: "under-500k", label: "Under 500K minutes / month" },
    { value: "500k-2m", label: "500K – 2M minutes / month" },
    { value: "2m-10m", label: "2M – 10M minutes / month" },
    { value: "10m-plus", label: "10M+ minutes / month" },
    { value: "not-sure", label: "Not sure yet" },
  ],
  need: [
    { value: "did", label: "DID / voice origination" },
    { value: "sip", label: "SIP trunking" },
  ],
  messenger: [
    { value: "telegram", label: "Telegram" },
    { value: "whatsapp", label: "WhatsApp" },
    { value: "email", label: "Email" },
    { value: "signal", label: "Signal" },
  ],
};

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError("");
    setLoading(true);

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("company_alt") as string;
    if (honeypot && honeypot.trim() !== "") {
      setLoading(false);
      setSubmitted(true);
      return;
    }

    formData.delete("company_alt");

    try {
      const res = await fetch("https://readdy.ai/api/form/d9tqd4f8l97n3eas3d20", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(
          Array.from(formData.entries(), ([k, v]) => [k, typeof v === "string" ? v : ""])
        ).toString(),
      });

      const text = await res.text();
      let parsed: any;
      try { parsed = JSON.parse(text); } catch { parsed = null; }

      if (res.ok && parsed?.code === "OK") {
        setSubmitted(true);
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || text || "Something went wrong. Please try again.";
        if (msg.toLowerCase().includes("spam") || msg.toLowerCase().includes("form data is spam")) {
          setFormError("Your submission could not be processed. Please try again.");
        } else {
          setFormError(msg);
        }
      }
    } catch {
      setFormError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-50 text-foreground-950">
      <Header />
      <main>
        {/* Hero */}
        <section className="pt-28 md:pt-36 pb-10 md:pb-14 overflow-hidden relative">
          <div className="absolute inset-0 vx-grid-bg pointer-events-none" />
          <div className="absolute -top-32 -right-32 w-[400px] h-[400px] rounded-full bg-accent-100/60 blur-3xl pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-5">
              <Link to="/" className="hover:text-primary-600 cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center" />
              <span className="text-foreground-800">Contact</span>
            </nav>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-semibold text-foreground-950 leading-tight tracking-tight max-w-4xl">
              Get rates. Start routing.
            </h1>
            <p className="mt-4 text-base md:text-lg text-foreground-700 max-w-3xl leading-relaxed">
              Tell us about your traffic profile and we will prepare a custom LCR quote with live quality data for your top destinations — typically within one business day.
            </p>
          </div>
        </section>

        {/* Form + Sidebar */}
        <section className="py-10 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              {/* Form */}
              <div className="lg:col-span-7">
                {submitted ? (
                  <div className="rounded-xl border border-accent-300 bg-accent-100 p-8 text-center">
                    <span className="w-16 h-16 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center mx-auto">
                      <i className="ri-check-line text-3xl w-10 h-10 flex items-center justify-center" />
                    </span>
                    <h2 className="mt-5 text-2xl font-semibold text-foreground-950 font-heading">Thank you.</h2>
                    <p className="mt-2 text-foreground-700 max-w-md mx-auto leading-relaxed">
                      Your inquiry has been received. Our carrier-relations team will review your traffic profile and respond within one business day — often within hours.
                    </p>
                    <Link
                      to="/"
                      className="mt-6 inline-flex items-center gap-2 bg-primary-500 text-background-50 px-5 py-2.5 rounded-md text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
                    >
                      Back to home
                      <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                    </Link>
                  </div>
                ) : (
                  <form
                    id="contact-form"
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="rounded-xl border border-background-200 bg-background-50 p-6 md:p-8"
                  >
                    <h2 className="text-xl font-semibold text-foreground-950 font-heading mb-6">
                      Your traffic profile
                    </h2>

                    {formError && (
                      <div className="mb-6 p-4 rounded-lg bg-primary-100 border border-primary-300 text-sm text-primary-900">
                        {formError}
                      </div>
                    )}

                    {/* Contact info */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">Full name *</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                          placeholder="Your name"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">Work email *</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                          placeholder="you@company.com"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                      <div>
                        <label htmlFor="company" className="block text-sm font-medium text-foreground-700 mb-1.5">Company name *</label>
                        <input
                          type="text"
                          id="company"
                          name="company"
                          required
                          className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                          placeholder="Your company"
                        />
                      </div>
                      <div>
                        <label htmlFor="title" className="block text-sm font-medium text-foreground-700 mb-1.5">Job title</label>
                        <input
                          type="text"
                          id="title"
                          name="title"
                          className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                          placeholder="e.g. Head of Carrier Relations"
                        />
                      </div>
                    </div>

                    {/* Lead qualification */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                      <div>
                        <label htmlFor="company_type" className="block text-sm font-medium text-foreground-700 mb-1.5">Company type *</label>
                        <select
                          id="company_type"
                          name="company_type"
                          required
                          className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent cursor-pointer"
                        >
                          <option value="">Select...</option>
                          {formFields.companyType.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="volume" className="block text-sm font-medium text-foreground-700 mb-1.5">Monthly volume *</label>
                        <select
                          id="volume"
                          name="volume"
                          required
                          className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent cursor-pointer"
                        >
                          <option value="">Select...</option>
                          {formFields.monthlyVolume.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="mb-6">
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">What do you need? *</label>
                      <div className="flex flex-wrap gap-3">
                        {formFields.need.map((o) => (
                          <label key={o.value} className="flex items-center gap-2 text-sm text-foreground-700 cursor-pointer">
                            <input type="checkbox" name="need" value={o.value} className="rounded border-background-300 text-primary-500 focus:ring-primary-400 cursor-pointer" />
                            {o.label}
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="mb-6">
                      <label htmlFor="countries" className="block text-sm font-medium text-foreground-700 mb-1.5">Top destination countries</label>
                      <input
                        type="text"
                        id="countries"
                        name="countries"
                        className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                        placeholder="e.g. India, Brazil, Mexico, Nigeria"
                      />
                    </div>

                    <div className="mb-6">
                      <label htmlFor="preferred_messenger" className="block text-sm font-medium text-foreground-700 mb-1.5">Preferred messenger for follow-up</label>
                      <select
                        id="preferred_messenger"
                        name="preferred_messenger"
                        className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent cursor-pointer"
                      >
                        <option value="">Select...</option>
                        {formFields.messenger.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </div>

                    <div className="mb-6">
                      <label htmlFor="messenger_id" className="block text-sm font-medium text-foreground-700 mb-1.5">Messenger handle / email</label>
                      <input
                        type="text"
                        id="messenger_id"
                        name="messenger_id"
                        className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                        placeholder="e.g. @username or email address"
                      />
                    </div>

                    <div className="mb-6">
                      <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">Anything else we should know?</label>
                      <textarea
                        id="message"
                        name="message"
                        rows={4}
                        maxLength={500}
                        className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent resize-none"
                        placeholder="Tell us about your routing requirements, technical preferences, or questions."
                      />
                      <p className="text-xs text-foreground-400 mt-1">500 characters max</p>
                    </div>

                    {/* Honeypot */}
                    <div className="vx-hp-shield" aria-hidden="true">
                      <label>
                        Alternate company
                        <input
                          type="text"
                          name="company_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                        />
                      </label>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-6 py-3 rounded-md text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                    >
                      {loading ? (
                        <>
                          <i className="ri-loader-4-line w-4 h-4 flex items-center justify-center animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          Get rates
                          <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-5">
                <div className="sticky top-28 space-y-6">
                  <div className="rounded-xl border border-background-200 bg-background-50 p-6">
                    <h3 className="text-base font-semibold text-foreground-950 font-heading mb-4">What happens next</h3>
                    <div className="space-y-4">
                      {[
                        { n: "1", text: "Our carrier-relations team reviews your traffic profile and destination mix." },
                        { n: "2", text: "We prepare a custom LCR quote with live quality data for your top destinations." },
                        { n: "3", text: "We reach out on your preferred messenger — typically within one business day." },
                        { n: "4", text: "Set up a test trunk and see ASR, PDD, and MOS on real calls. No hard sell." },
                      ].map((s) => (
                        <div key={s.n} className="flex gap-3">
                          <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center shrink-0 text-xs font-bold">{s.n}</span>
                          <p className="text-sm text-foreground-700 leading-snug">{s.text}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-xl border border-background-200 bg-secondary-50 p-6">
                    <h3 className="text-base font-semibold text-foreground-950 font-heading mb-4">Contact info</h3>
                    <ul className="space-y-3 text-sm text-foreground-700">
                      <li className="flex gap-3">
                        <i className="ri-map-pin-2-line text-primary-600 w-5 h-5 flex items-center justify-center shrink-0 mt-0.5" />
                        <span className="leading-relaxed">
                          3960 Howard Hughes Parkway
                          <br />
                          Suite 500
                          <br />
                          Las Vegas, Nevada 89169
                        </span>
                      </li>
                      <li className="flex items-center gap-3">
                        <i className="ri-phone-line text-primary-600 w-5 h-5 flex items-center justify-center shrink-0" />
                        <span>
                          <span className="text-foreground-500">Telephone:</span>{" "}
                          <a href="tel:+17025095030" className="hover:text-primary-600 cursor-pointer">+1-702-509-5030</a>
                        </span>
                      </li>
                      <li className="flex items-center gap-3">
                        <i className="ri-phone-line text-primary-600 w-5 h-5 flex items-center justify-center shrink-0" />
                        <span>
                          <span className="text-foreground-500">Toll Free:</span>{" "}
                          <a href="tel:8009441190" className="hover:text-primary-600 cursor-pointer">800-944-1190</a>
                        </span>
                      </li>
                      <li className="flex items-center gap-3">
                        <i className="ri-printer-line text-primary-600 w-5 h-5 flex items-center justify-center shrink-0" />
                        <span>
                          <span className="text-foreground-500">Fax number:</span>{" "}
                          <span>+1-702-553-4346</span>
                        </span>
                      </li>
                    </ul>
                  </div>

                  <div className="rounded-xl border border-background-200 bg-secondary-50 p-6">
                    <h3 className="text-base font-semibold text-foreground-950 font-heading mb-4">Support info</h3>
                    <ul className="space-y-3 text-sm text-foreground-700">
                      <li className="flex items-center gap-3">
                        <i className="ri-phone-line text-primary-600 w-5 h-5 flex items-center justify-center shrink-0" />
                        <span>
                          <span className="text-foreground-500">Telephone:</span>{" "}
                          <a href="tel:+17029830030" className="hover:text-primary-600 cursor-pointer">+1-702-983-0030</a>
                        </span>
                      </li>
                      <li className="flex items-center gap-3">
                        <i className="ri-mail-line text-primary-600 w-5 h-5 flex items-center justify-center shrink-0" />
                        <span>
                          <span className="text-foreground-500">Email:</span>{" "}
                          <a href="mailto:noc@voxmaster.com" className="hover:text-primary-600 cursor-pointer">noc@voxmaster.com</a>
                        </span>
                      </li>
                    </ul>
                  </div>

                  <div className="rounded-xl border border-background-200 bg-background-50 p-6">
                    <h3 className="text-base font-semibold text-foreground-950 font-heading mb-2">No minimum commitment</h3>
                    <p className="text-sm text-foreground-700 leading-relaxed">
                      You can start with any volume. For operators routing more than 1M minutes per month, we offer tiered volume discounts. Test the routes first, scale when you are ready.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}